import { useMemo, useState } from "react";
import classNames from "classnames";
import { Pie, PieChart, ResponsiveContainer, Sector, Tooltip, type PieSectorShapeProps } from "recharts";
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  Chip,
  Division,
  Flex,
  Heading,
  Icons,
  Paragraph,
  Skeleton,
  Span,
} from "@ir/ui";
import { useAnalyticsKPI, useAnalyticsMobile } from "@/hooks";
import { ANALYTICS_CHECK_DATE_RANGE_LABEL } from "@/store/analytics";
import { IAgingBracketItem, IAgingBracketsSection } from "@/core/repositories/analytics";
import EmptyKpiCard from "./EmptyKpiCard";
import "../../styles/Analytics.page.scss";

type SectionKey = "allOpen" | "inDispute";

const BRACKET_COLORS = ["var(--primary-300)", "var(--warning-300)", "var(--error-300)", "var(--error-500)"];
const BRACKET_COLORS_TEXT = ["var(--primary-500)", "var(--warning-600)", "var(--error-400)", "var(--error-600)"];

const HEADING = "Deductions by Aging Brackets";

const renderAgingPieSector = (props: PieSectorShapeProps) => <Sector {...props} />;

const formatCompactCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  })
    .format(value)
    .replace(/^/, "$");

const formatCurrencyFull = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

const getBracketColor = (index: number) => BRACKET_COLORS[index % BRACKET_COLORS.length];
const getBracketColorText = (index: number) => BRACKET_COLORS_TEXT[index % BRACKET_COLORS_TEXT.length];

const SECTION_CENTER_LABEL: Record<SectionKey, string> = {
  allOpen: "open",
  inDispute: "in dispute",
};

const AGING_SKELETON_BRACKETS = ["bracket-1", "bracket-2", "bracket-3", "bracket-4"] as const;

type DonutSlice = IAgingBracketItem & { fill: string };

const TABLE_LABEL_COLUMN_WIDTH = 80;
const TABLE_AMOUNT_COLUMN_WIDTH = 110;
const TABLE_SHARE_COLUMN_WIDTH = 50;
const TABLE_COUNT_COLUMN_WIDTH = 50;

const AgingDonutTooltip = ({ active, payload }: { active?: boolean; payload?: { payload: DonutSlice }[] }) => {
  if (!active || !payload?.length) return null;

  const item = payload[0].payload;

  return (
    <Division
      style={{
        background: "var(--grey-900)",
        borderRadius: 8,
        padding: "10px 14px",
        color: "var(--white-color)",
        fontSize: 13,
        minWidth: 180,
      }}
    >
      <Flex align="center" gap={0.5} style={{ fontWeight: 600, marginBottom: 6 }}>
        <Span
          className="d-inline-block"
          style={{ height: 12, width: 12, borderRadius: 2, backgroundColor: item.fill }}
        />
        {item.bracketLabel}
      </Flex>
      <Division style={{ marginBottom: 4 }}>{formatCurrencyFull(item.amount)}</Division>
      <Division style={{ marginBottom: 4 }}>Count: {item.count.toLocaleString()}</Division>
      <Division style={{ color: "var(--grey-300)" }}>{item.share?.toFixed(2)}% share</Division>
    </Division>
  );
};

const AgingBracketRowSkeleton = () => (
  <tr>
    <td style={{ width: TABLE_LABEL_COLUMN_WIDTH, paddingBottom: 16 }}>
      <Skeleton width={48} height={12} style={{ borderRadius: 4 }} />
    </td>
    <td style={{ paddingBottom: 16 }}>
      <Skeleton height={10} style={{ width: "100%", borderRadius: 4 }} />
    </td>
    <td style={{ width: TABLE_AMOUNT_COLUMN_WIDTH, paddingBottom: 16 }} className="text-right">
      <Flex justifyContent="end">
        <Skeleton width={56} height={14} style={{ borderRadius: 4 }} />
      </Flex>
    </td>
    <td style={{ width: TABLE_SHARE_COLUMN_WIDTH, paddingBottom: 16 }} className="text-right">
      <Flex justifyContent="end">
        <Skeleton width={28} height={12} style={{ borderRadius: 4 }} />
      </Flex>
    </td>
    <td style={{ width: TABLE_COUNT_COLUMN_WIDTH, paddingBottom: 16 }} className="text-right">
      <Flex justifyContent="end">
        <Skeleton width={36} height={12} style={{ borderRadius: 4 }} />
      </Flex>
    </td>
  </tr>
);

const AgingBracketsTableHeader = () => (
  <thead>
    <tr>
      <th style={{ width: TABLE_LABEL_COLUMN_WIDTH, paddingBottom: 10 }} />
      <th style={{ paddingBottom: 10 }} />
      <th style={{ width: TABLE_AMOUNT_COLUMN_WIDTH, paddingBottom: 10 }} className="text-right">
        <Span className="text-grey-400 text-sm d-inline-block font-bold px-2">Amount</Span>
      </th>
      <th style={{ width: TABLE_SHARE_COLUMN_WIDTH, paddingBottom: 10 }} className="text-right">
        <Span className="text-grey-400 text-sm d-inline-block font-bold">Share</Span>
      </th>
      <th style={{ width: TABLE_COUNT_COLUMN_WIDTH, paddingBottom: 10 }} className="text-right">
        <Span className="text-grey-400 text-sm d-inline-block font-bold">Count</Span>
      </th>
    </tr>
  </thead>
);

const AgingBracketsSkeleton = () => (
  <Flex direction="row" gap={1} style={{ minHeight: 320 }} align="center" wrap="wrap">
    <Division style={{ flex: 2, minWidth: 0 }}>
      <table className="aging-brackets-table">
        <AgingBracketsTableHeader />
        <tbody>
          {AGING_SKELETON_BRACKETS.map(key => (
            <AgingBracketRowSkeleton key={key} />
          ))}
        </tbody>
      </table>
    </Division>
    <Division style={{ flex: 1, minWidth: 0, maxWidth: 280, position: "relative" }}>
      <Flex align="center" justifyContent="center" style={{ height: 280 }}>
        <Skeleton width={180} height={180} style={{ borderRadius: "50%" }} />
      </Flex>
      <Flex
        direction="column"
        align="center"
        justifyContent="center"
        gap={0.5}
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
        }}
      >
        <Skeleton width={72} height={20} style={{ borderRadius: 4 }} />
        <Skeleton width={44} height={12} style={{ borderRadius: 4 }} />
      </Flex>
    </Division>
  </Flex>
);

const BracketRow = ({ bracket, fill, textColor }: { bracket: IAgingBracketItem; fill: string; textColor: string }) => {
  const barWidth = Math.min(Math.max(bracket.share, 0), 100);

  return (
    <tr>
      <td style={{ width: TABLE_LABEL_COLUMN_WIDTH }}>
        <Span className="text-grey-400" style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap" }}>
          {bracket.bracketLabel}
        </Span>
      </td>
      <td>
        <Division
          style={{
            height: 10,
            borderRadius: 4,
            backgroundColor: "var(--grey-50)",
            overflow: "hidden",
          }}
        >
          <Division
            style={{
              width: `${barWidth}%`,
              height: "100%",
              borderRadius: 4,
              backgroundColor: fill,
              transition: "width 1.5s ease 0s",
            }}
          />
        </Division>
      </td>
      <td style={{ width: TABLE_AMOUNT_COLUMN_WIDTH }} className="text-right">
        <Span className="font-bold text-md d-inline-block px-2" style={{ color: textColor }}>
          {formatCompactCurrency(bracket.amount)}
        </Span>
      </td>
      <td style={{ width: TABLE_SHARE_COLUMN_WIDTH }} className="text-right">
        <Span className="text-grey-400 text-sm d-inline-block font-bold">{bracket.share?.toFixed(2)}%</Span>
      </td>
      <td style={{ width: TABLE_COUNT_COLUMN_WIDTH }} className="text-right">
        <Span className="text-grey-400 text-sm d-inline-block font-bold">{bracket.count.toLocaleString()}</Span>
      </td>
    </tr>
  );
};

const AgingDonutChart = ({ section, sectionKey }: { section: IAgingBracketsSection; sectionKey: SectionKey }) => {
  const donutData = useMemo(
    () =>
      section.brackets.map((bracket, index) => ({
        ...bracket,
        fill: getBracketColor(index),
      })),
    [section.brackets]
  );

  return (
    <Division style={{ position: "relative", width: "100%", height: 280 }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={donutData}
            dataKey="amount"
            nameKey="bracketLabel"
            cx="50%"
            cy="50%"
            innerRadius="58%"
            outerRadius="82%"
            paddingAngle={0}
            stroke="none"
            shape={renderAgingPieSector}
          />
          <Tooltip wrapperStyle={{ zIndex: 2 }} content={<AgingDonutTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      <Flex
        direction="column"
        align="center"
        justifyContent="center"
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        <Heading text={formatCompactCurrency(section.totalAmount)} level={5} className="m-0 text-grey-800 font-bold" />
        <Span className="text-grey-400" style={{ fontSize: 13 }}>
          {SECTION_CENTER_LABEL[sectionKey]}
        </Span>
      </Flex>
    </Division>
  );
};

const DeductionsByAgingBracketsCard = () => {
  const [selectedSection, setSelectedSection] = useState<SectionKey>("allOpen");
  const { deductionsByAgingBrackets } = useAnalyticsKPI();
  const isMobile = useAnalyticsMobile();

  const activeSection = useMemo((): IAgingBracketsSection | null => {
    if (!deductionsByAgingBrackets.data) return null;
    return deductionsByAgingBrackets.data[selectedSection];
  }, [deductionsByAgingBrackets.data, selectedSection]);

  const hasSectionData =
    !!activeSection && activeSection.brackets.length > 0 && activeSection.totalAmount > 0 && activeSection.totalCount > 0;

  const cardContent = (() => {
    if (deductionsByAgingBrackets.isLoading) {
      return <AgingBracketsSkeleton />;
    }

    if (!hasSectionData) {
      return (
        <Flex justifyContent="center" align="center" style={{ height: 350 }}>
          <EmptyKpiCard
            title="No data available"
            description="No deductions recorded for the selected period. Try adjusting the date range."
            icon={Icons.BarChart5}
          />
        </Flex>
      );
    }

    return (
      <Flex direction="row" gap={1} className="analytics-pie-composite" style={{ minHeight: 320 }} align="center" wrap="wrap">
        <Division style={{ flex: 2, minWidth: 0 }}>
          <table className="aging-brackets-table" align="right">
            <AgingBracketsTableHeader />
            <tbody>
              {activeSection.brackets.map((bracket, index) => (
                <BracketRow key={bracket.bracketKey} bracket={bracket} fill={getBracketColor(index)} textColor={getBracketColorText(index)} />
              ))}
            </tbody>
          </table>
        </Division>
        <Division style={{ flex: 1, minWidth: 0, maxWidth: 280 }}>
          <AgingDonutChart section={activeSection} sectionKey={selectedSection} />
        </Division>
      </Flex>
    );
  })();

  return (
    <Card className="bg-white border-1 border-color-grey-50">
      <Flex
        className="analytics-kpi-card__header px-4 py-3 border-bottom-1 border-color-grey-25"
        justifyContent="space-between"
        align="center"
      >
        <Flex direction="column">
          <Heading text={HEADING} level={6} className="m-0 text-grey-500 font-bold" />
          {!isMobile && (
            <Paragraph className="m-0 text-grey-400">Open deductions grouped by age</Paragraph>
          )}
        </Flex>
        {!deductionsByAgingBrackets.isLoading && <Button.Group className="p-1 bg-grey-10 border-1 border-color-grey-100 rounded-small">
          <Button
            size="small"
            color="grey"
            variant={selectedSection === "allOpen" ? "solid" : "clear"}
            onPress={() => setSelectedSection("allOpen")}
            className={classNames("m-0", {
              "bg-grey-25": selectedSection === "allOpen",
              "text-grey-600": selectedSection === "allOpen",
            })}
          >
            All Open
          </Button>
          <Button
            size="small"
            color="grey"
            variant={selectedSection === "inDispute" ? "solid" : "clear"}
            onPress={() => setSelectedSection("inDispute")}
            className={classNames("m-0", {
              "bg-grey-25": selectedSection === "inDispute",
              "text-grey-600": selectedSection === "inDispute",
            })}
          >
            In Dispute
          </Button>
        </Button.Group>}
      </Flex>
      <CardContent style={{ minHeight: 370 }}>{cardContent}</CardContent>
      <CardFooter
        className="analytics-card-footer bg-grey-10 text-grey-500"
        style={{ borderBottomLeftRadius: ".75rem", borderBottomRightRadius: ".75rem" }}
      >
        <Flex justifyContent="space-between" align="center" gap={0.5} wrap="wrap">
          Filter applied:{" "}
          <Chip label={ANALYTICS_CHECK_DATE_RANGE_LABEL} variant="filled" color="secondary" borderless />
          <Chip label="Purchaser" variant="filled" color="secondary" borderless />
        </Flex>
      </CardFooter>
    </Card>
  );
};

export default DeductionsByAgingBracketsCard;
